import { IResult } from '../models/result-models';

let authToken: string | null = null;

const FORM_ERROR = 'form';

export enum HttpMethods {
    PUT = 'PUT',
    POST = 'POST',
    DELETE = 'DELETE',
}

export const API_BASE_URL = process.env.REACT_APP_API_URL;

export const setToken = (token: string) => {
    authToken = token;
};

const tryFetch = async (url: string, options: RequestInit) => {
    try {
        return await fetch(url, options);
    } catch (error) {
        if (error instanceof Error && error.message === 'Unauthorized') {
            return {
                ok: false,
                status: 401,
            } as Response;
        }
        throw error;
    }
};

export const handleResponse = async <T>(
    response: Response
): Promise<IResult<T>> => {
    // tslint:disable-next-line: no-console
    if (response.ok) {
        const data = (await response.json()) as T;
        return {
            ok: true,
            data,
        };
    } else if (response.status === 401) {
        // eslint-disable-next-line no-restricted-globals
        const path = location;
        window.location.href = '/login?' + path;
        sessionStorage.clear();
        return {
            ok: false,
            errors: {},
        };
    } else if (response.status === 400) {
        const data = await response.json();
        const result: IResult<T> = {
            ok: false,
            errors: {},
        };

        for (const prop in data) {
            if (Object.prototype.hasOwnProperty.call(data, prop)) {
                result.errors[FORM_ERROR] = data[prop].toString();
            } else {
                result.errors[prop] = data[prop].toString();
            }
        }
        return result;
    } else if (response.status === 302) {
        window.location.href = '/';
        throw new Error('Redirect');
    } else {
        const error = await response.text();
        throw new Error(error);
    }
};

export const apiGet = async <T>(
    url: string,
    auth = true
): Promise<IResult<T>> => {
    const options: RequestInit = auth
        ? {
              credentials: 'include',
              headers: { Authorization: 'bearer ' + authToken },
          }
        : {};
    const response = await tryFetch(url, options);
    const result = await handleResponse<T>(response);
    return result;
};

export const apiDelete = async <T>(url: string): Promise<IResult<T>> => {
    const options: RequestInit = {
        credentials: 'include',
        headers: { Authorization: 'bearer ' + authToken },
        method: HttpMethods.DELETE,
    };
    const response = await tryFetch(url, options);
    const result = await handleResponse<T>(response);
    return result;
};

const apiSubmit = async <R, T>(
    url: string,
    request: R,
    httpMethod: string
): Promise<IResult<T>> => {
    const response = await tryFetch(url, {
        method: httpMethod,
        headers: {
            'content-type': 'application/json',
            Authorization: 'bearer ' + authToken,
        },
        credentials: 'include',
        body: JSON.stringify(request),
    });
    const result = await handleResponse<T>(response);
    return result;
};

/**
 *
 * @param url url to call
 * @param item object to send on the body of the post request
 */
export const apiPost = async <R, T>(
    url: string,
    item: R
): Promise<IResult<T>> => {
    return apiSubmit(url, item, HttpMethods.POST);
};

/**
 *
 * @param url url to call
 * @param item object to send on the body of the put request
 */
export const apiPut = async <R, T>(
    url: string,
    item: R
): Promise<IResult<T>> => {
    return apiSubmit(url, item, HttpMethods.PUT);
};

/**
 *
 * @param url url to call
 * @param item object to send on the body of the post request
 */
export const apiPostFileUpload = async <T>(
    url: string,
    item: FormData
): Promise<IResult<T>> => {
    const response = await tryFetch(url, {
        method: HttpMethods.POST,
        headers: {
            Authorization: 'bearer ' + authToken,
        },
        credentials: 'include',
        body: item,
    });

    return await handleResponse<T>(response);
};

export const apiPostJson = async (
    url: string,
    item: any
): Promise<Response> => {
    const response = await tryFetch(url, {
        method: HttpMethods.POST,
        headers: {
            Authorization: 'bearer ' + authToken,
            'content-type': 'application/json',
        },
        credentials: 'include',
        body: item,
    });

    return response;
};

export const apiGetPDF = async (
    url: string,
    auth = true
): Promise<Response> => {
    const options: RequestInit = auth
        ? {
              credentials: 'include',
              headers: { Authorization: 'bearer ' + authToken },
          }
        : {};
    const response = await tryFetch(url, options);

    return response;
};

export const apiOpenPDF = async (result: Response) => {
    const arrayResult = await result.arrayBuffer();
    const data = new Blob([arrayResult], {
        type: 'application/pdf',
    });
    const pdfUrl = URL.createObjectURL(data);
    window.open(pdfUrl);
};
